import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { localizedValue } from './nationalizationViewModel';

const makeEstablishment = (countryCode = '', index = 0) => ({
  id: `establishment_${Date.now()}_${index}`,
  name: '',
  country_code: countryCode,
  regulator_establishment_id: '',
  zone_type: '',
  location: { city: '', region: '' },
  activity_codes: [],
  government_contract: null,
  custom_facts: {}
});

const getAtPath = (source, path) => String(path || '').split('.').reduce(
  (value, key) => value?.[key],
  source
);

const setAtPath = (source, path, value) => {
  const keys = String(path || '').split('.').filter(Boolean);
  const next = { ...(source || {}) };
  let cursor = next;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    cursor[key] = { ...(cursor[key] || {}) };
    cursor = cursor[key];
  });
  return next;
};

const countryIsAvailable = (country = {}) => (
  country.availability_status === 'available'
  // Keep Saudi usable while frontend and backend deployments roll out. Every
  // other country fails closed when availability metadata is missing.
  || (!country.availability_status && country.code === 'SA')
);

const normalizeProfile = (profile = {}, fallbackCountry = '') => {
  const countryCode = profile?.country_code || fallbackCountry || '';
  const establishments = Array.isArray(profile?.establishments) && profile.establishments.length
    ? profile.establishments
    : [makeEstablishment(countryCode)];
  const normalizedEstablishments = establishments.map((establishment, index) => ({
    ...makeEstablishment(countryCode, index),
    ...establishment,
    id: establishment?.id || `establishment_${index + 1}`,
    country_code: establishment?.country_code || countryCode,
    location: { city: '', region: '', ...(establishment?.location || {}) },
    activity_codes: Array.isArray(establishment?.activity_codes) ? establishment.activity_codes : [],
    custom_facts: { ...(establishment?.custom_facts || {}) }
  }));
  const requestedPrimaryId = profile?.primary_establishment_id || '';
  const primaryEstablishmentId = normalizedEstablishments.some(({ id }) => id === requestedPrimaryId)
    ? requestedPrimaryId
    : normalizedEstablishments[0]?.id || '';

  return {
    country_code: countryCode,
    legal_name: profile?.legal_name || '',
    primary_establishment_id: primaryEstablishmentId,
    establishments: normalizedEstablishments
  };
};

const normalizeProfileForRollout = (profile, countries, fallbackCountry = '') => {
  const normalized = normalizeProfile(profile, fallbackCountry);
  if (!normalized.country_code) return normalized;
  const selectedCountry = countries.find(({ code }) => code === normalized.country_code);
  if (selectedCountry && countryIsAvailable(selectedCountry)) return normalized;

  // Preserve names loaded from the company database, but do not carry legal
  // facts from a coming-soon country into the Saudi setup.
  return {
    ...normalized,
    country_code: '',
    establishments: normalized.establishments.map((establishment, index) => ({
      ...makeEstablishment('', index),
      id: establishment.id || `establishment_${index + 1}`,
      name: establishment.name || '',
      country_code: ''
    }))
  };
};

function ProfileField({ field, value, language, t, onChange, idSuffix = '' }) {
  const label = localizedValue(field, language) || t(`profile.fields.${field.key}`, field.key);
  const helpText = localizedValue(field, language, 'help_text');
  const options = Array.isArray(field?.options) ? field.options : [];
  const inputId = `nationalization-profile-${idSuffix}-${String(field.key).replace(/[^a-z0-9]/gi, '-')}`;

  if (field.type === 'boolean') {
    return (
      <label className="nationalization-form-field" htmlFor={inputId}>
        <span>{uiText(label)}{field.required && <b>*</b>}</span>
        <select id={inputId} value={value === true ? 'true' : value === false ? 'false' : ''} onChange={(event) => {
          onChange(event.target.value === '' ? null : event.target.value === 'true');
        }}>
          <option value="">{t('common.notProvided', 'Not provided')}</option>
          <option value="true">{t('common.yes', 'Yes')}</option>
          <option value="false">{t('common.no', 'No')}</option>
        </select>
        {helpText && <small>{helpText}</small>}
      </label>
    );
  }

  if (field.type === 'multi_select' && options.length > 0) {
    const selectedValues = Array.isArray(value) ? value : [];
    return (
      <label className="nationalization-form-field" htmlFor={inputId}>
        <span>{uiText(label)}{field.required && <b>*</b>}</span>
        <select
          id={inputId}
          multiple
          value={selectedValues}
          onChange={(event) => onChange(Array.from(event.target.selectedOptions).map((option) => option.value))}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {localizedValue(option, language) || option.value}
            </option>
          ))}
        </select>
        {helpText && <small>{helpText}</small>}
      </label>
    );
  }

  if (options.length || field.type === 'select') {
    return (
      <label className="nationalization-form-field" htmlFor={inputId}>
        <span>{uiText(label)}{field.required && <b>*</b>}</span>
        <select id={inputId} value={value ?? ''} onChange={(event) => onChange(event.target.value)}>
          <option value="">{t('common.select', 'Select')}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {localizedValue(option, language) || option.value}
            </option>
          ))}
        </select>
        {helpText && <small>{helpText}</small>}
      </label>
    );
  }

  // Some official activity-code fields intentionally have no fixed option
  // catalogue. In that case a comma-separated input remains usable instead of
  // rendering an empty multi-select that the employer cannot complete.
  const isList = field.type === 'array' || field.type === 'multi_select' || field.key === 'activity_codes';
  return (
    <label className="nationalization-form-field" htmlFor={inputId}>
      <span>{uiText(label)}{field.required && <b>*</b>}</span>
      <input
        id={inputId}
        type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
        value={isList ? (Array.isArray(value) ? value.join(', ') : value || '') : value ?? ''}
        placeholder={localizedValue(field, language, 'placeholder')}
        onChange={(event) => onChange(
          isList
            ? event.target.value.split(',').map((item) => item.trim()).filter(Boolean)
            : field.type === 'number' && event.target.value !== ''
              ? Number(event.target.value)
              : event.target.value
        )}
      />
      {helpText && <small>{helpText}</small>}
    </label>
  );
}

export default function NationalizationSetupForm({
  countries = [],
  initialProfile,
  language,
  saving,
  error,
  t,
  onSave,
  onCancel
}) {
  useUiLanguage();
  // Never default an unknown company to the first supported jurisdiction. The
  // employer must explicitly choose a country when no saved/legacy profile can
  // be resolved, otherwise UAE rules could appear for an unrelated location.
  const fallbackCountry = '';
  const [profile, setProfile] = useState(() => normalizeProfileForRollout(initialProfile, countries, fallbackCountry));
  const [setupPage, setSetupPage] = useState(0);
  const [pageTwoTopOffset, setPageTwoTopOffset] = useState(null);
  const setupPanelRef = useRef(null);

  useEffect(() => {
    setProfile(normalizeProfileForRollout(initialProfile, countries, fallbackCountry));
    setSetupPage(0);
    setPageTwoTopOffset(null);
  }, [countries, fallbackCountry, initialProfile]);

  const orderedCountries = useMemo(() => [...countries].sort((left, right) => (
    Number(countryIsAvailable(right)) - Number(countryIsAvailable(left))
  )), [countries]);

  const countryConfig = useMemo(
    () => countries.find((country) => country.code === profile.country_code) || null,
    [countries, profile.country_code]
  );
  const isSaudiSetup = profile.country_code === 'SA';
  const governmentStepTitle = isSaudiSetup
    ? t('setup.saudiEntitiesTitle', 'Qiwa entities')
    : t('setup.establishmentsTitle', 'Company and government details');
  const profileFields = (countryConfig?.profile_fields || []).filter((field) => ![
    'country_code',
    'legal_name',
    'name'
  ].includes(field.key));

  // Qatar's law does not make a bare "exempt" selection enough evidence. These
  // two fields become required in the browser exactly when the backend requires
  // them, so the user sees the dependency before submitting the profile.
  const targetFieldContext = (field, establishment) => {
    const match = String(field?.key || '').match(/^(.*official_target)\.(.+)$/);
    if (!match) return null;
    return {
      prefix: match[1],
      fieldName: match[2],
      requirementType: getAtPath(establishment, `${match[1]}.requirement_type`)
    };
  };

  const targetFieldIsVisible = (field, establishment) => {
    const context = targetFieldContext(field, establishment);
    if (!context || context.fieldName === 'requirement_type') return true;
    if (!context.requirementType) return false;
    const matchingType = {
      required_rate_pct: 'fixed_rate',
      required_count: 'fixed_count',
      official_band: 'official_band'
    }[context.fieldName];
    return !matchingType || matchingType === context.requirementType;
  };

  const fieldIsRequired = (field, establishment) => {
    const targetContext = targetFieldContext(field, establishment);
    const selectedTargetValue = targetContext && {
      fixed_rate: 'required_rate_pct',
      fixed_count: 'required_count',
      official_band: 'official_band'
    }[targetContext.requirementType];
    return field.required
    || (targetContext && targetContext.fieldName === selectedTargetValue)
    || (
      establishment?.excluded_by_authority === true
      && [
        'custom_facts.exemption_source_reference',
        'custom_facts.exemption_verified_at'
      ].includes(field.key)
    );
  };

  const changeCountry = (countryCode) => {
    // Country-specific legal facts cannot safely move between jurisdictions.
    // Keep only the branch's human label and clear every location and legal
    // fact. A governorate, region or free-zone value from one country must never
    // silently become evidence for another country's calculation.
    setProfile((current) => ({
      ...current,
      country_code: countryCode,
      establishments: current.establishments.map((establishment, index) => ({
        ...makeEstablishment(countryCode, index),
        id: establishment.id || `establishment_${index + 1}`,
        name: establishment.name || current.legal_name || '',
        country_code: countryCode
      }))
    }));
    setSetupPage(0);
  };

  const updateEstablishment = (index, updater) => {
    setProfile((current) => ({
      ...current,
      establishments: current.establishments.map((establishment, itemIndex) => (
        itemIndex === index ? updater(establishment) : establishment
      ))
    }));
  };

  const establishmentHasRequiredFields = (establishment) => (
    profileFields
      .filter((field) => fieldIsRequired(field, establishment))
      .every((field) => {
        if (establishment.excluded_by_authority === true && field.key.startsWith('official_target.')) {
          return true;
        }
        const value = getAtPath(establishment, field.key);
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'boolean') return true;
        return value !== null && value !== undefined && String(value).trim() !== '';
      })
  );
  const hasRequiredCountryFields = profile.establishments.every(establishmentHasRequiredFields);
  const isCompanyStepValid = Boolean(profile.country_code && profile.legal_name.trim());
  const isValid = Boolean(
    isCompanyStepValid
    && profile.establishments.length
    && profile.establishments.every((establishment) => establishment.name?.trim())
    && hasRequiredCountryFields
  );
  // Setup is always a two-step flow, including before a country is selected.
  // Keeping the structure stable avoids the form changing shape mid-entry.
  const showCompanySection = setupPage === 0;
  const showEstablishmentsSection = setupPage === 1;

  const continueToGovernmentDetails = () => {
    const panel = setupPanelRef.current;
    const parent = panel?.parentElement;
    if (panel && parent) {
      // Preserve Step 1's visible top edge when the taller second step opens.
      // Step 2 then grows downward instead of being centered above the viewport.
      const offset = panel.getBoundingClientRect().top - parent.getBoundingClientRect().top;
      setPageTwoTopOffset(Math.max(20, offset));
    }
    setSetupPage(1);
  };

  return (
    <section
      ref={setupPanelRef}
      className={`nationalization-setup-panel${setupPage === 1 ? ' nationalization-setup-panel--page-two' : ''}`}
      style={setupPage === 1 && pageTwoTopOffset !== null ? { marginTop: `${pageTwoTopOffset}px` } : undefined}
    >
      <div className="nationalization-setup-heading">
        <div>
          <h3>{t('setup.title', 'Set up nationalization')}</h3>
          <p>{t('setup.description', 'Choose a country and add your company details.')}</p>
        </div>
        {onCancel && (
          <button type="button" className="nationalization-icon-button" onClick={onCancel} aria-label={t('actions.close', 'Close')}>
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="nationalization-setup-progress" role="group" aria-label={t('setup.progressLabel', 'Setup progress')}>
        <div className={setupPage === 0 ? 'active' : 'complete'}>
          <span>1</span>
          <strong>{t('setup.companyDetailsTitle', 'Company details')}</strong>
        </div>
        <span className="nationalization-setup-progress-line" aria-hidden="true" />
        <div className={setupPage === 1 ? 'active' : ''}>
          <span>2</span>
          <strong>{governmentStepTitle}</strong>
        </div>
      </div>

      {showCompanySection && <div className="nationalization-setup-section">
        <div className="nationalization-setup-section-heading">
          <div>
            <h4>{t('setup.companyDetailsTitle', 'Company details')}</h4>
            <p>{t('setup.companyDetailsDescription', 'Enter the details shown on your official company records.')}</p>
          </div>
        </div>

        <div className="nationalization-form-grid">
          <label className="nationalization-form-field">
            <span>{t('profile.fields.country_code', 'Country')}<b>*</b></span>
            <select value={profile.country_code} onChange={(event) => changeCountry(event.target.value)}>
              <option value="">{t('common.selectCountry', 'Select a country')}</option>
              {orderedCountries.map((country) => (
                <option key={country.code} value={country.code} disabled={!countryIsAvailable(country)}>
                  {localizedValue(country, language) || t(`countries.${country.code}`, country.code)}
                  {!countryIsAvailable(country) ? ` — ${t('common.comingSoon', 'Coming soon')}` : ''}
                  {countryIsAvailable(country) && country.coverage?.status_code === 'beta'
                    ? ` — ${t('common.beta', 'Beta')}`
                    : ''}
                </option>
              ))}
            </select>
            <small>{t('setup.countryHelp', 'Choose the country where the company or branch is registered.')}</small>
          </label>
          <label className="nationalization-form-field">
            <span>{t('profile.fields.legal_name', 'Legal company name')}<b>*</b></span>
            <input
              value={profile.legal_name}
              placeholder={t('setup.legalNamePlaceholder', 'Enter the official company name')}
              onChange={(event) => setProfile((current) => ({
                ...current,
                legal_name: event.target.value,
                establishments: current.establishments.map((establishment) => ({
                  ...establishment,
                  name: !establishment.name || establishment.name === current.legal_name
                    ? event.target.value
                    : establishment.name
                }))
              }))}
            />
          </label>
        </div>

        {countryConfig && (
          <div className="nationalization-country-guidance">
            <i className="fa-solid fa-circle-info" aria-hidden="true" />
            <span>{t(`countryRequirements.${countryConfig.code}`, '')}</span>
          </div>
        )}
      </div>}

      {showEstablishmentsSection && <div className="nationalization-setup-section">
        <div className="nationalization-setup-section-heading nationalization-establishments-heading">
          <div>
            <h4>{governmentStepTitle}</h4>
            <p>{isSaudiSetup
              ? t('setup.saudiEntitiesDescription', 'Add each Qiwa entity that has its own entity number or Nitaqat activity.')
              : t('setup.establishmentsDescription', 'Enter the official details used to calculate your target.')}</p>
          </div>
        </div>

        {countryConfig && (
          <div className="nationalization-country-guidance nationalization-country-guidance--form">
            <i className="fa-solid fa-circle-info" aria-hidden="true" />
            <span>{t(`countryEntryInstructions.${countryConfig.code}`, '')}</span>
          </div>
        )}

        <div className="nationalization-establishments">
          {profile.establishments.map((establishment, index) => (
            <article key={establishment.id || index} className="nationalization-establishment">
              <header className="nationalization-establishment-header">
                <div>
                  <span className="nationalization-establishment-index" aria-hidden="true">{index + 1}</span>
                  <strong>{isSaudiSetup
                    ? t('setup.saudiEntityNumber', 'Qiwa entity {{number}}', { number: index + 1 })
                    : t('setup.establishmentNumber', 'Company / branch {{number}}', { number: index + 1 })}</strong>
                </div>
                <div className="nationalization-establishment-actions">
                  <label className="nationalization-primary-establishment-choice">
                    <input
                      type="radio"
                      name="nationalization-primary-establishment"
                      checked={profile.primary_establishment_id === establishment.id}
                      onChange={() => setProfile((current) => ({
                        ...current,
                        primary_establishment_id: establishment.id
                      }))}
                    />
                    <span>{t('setup.primaryEstablishmentShort', 'Primary')}</span>
                  </label>
                  {profile.establishments.length > 1 && (
                    <button
                      type="button"
                      className="nationalization-icon-button danger"
                      aria-label={t('actions.removeEstablishment', 'Remove company / branch')}
                      title={t('actions.removeEstablishment', 'Remove company / branch')}
                      onClick={() => setProfile((current) => ({
                        ...current,
                        ...(() => {
                          const remaining = current.establishments.filter((_, itemIndex) => itemIndex !== index);
                          return {
                            establishments: remaining,
                            primary_establishment_id: remaining.some(({ id }) => id === current.primary_establishment_id)
                              ? current.primary_establishment_id
                              : remaining[0]?.id || ''
                          };
                        })()
                      }))}
                    >
                      <i className="fa-solid fa-trash" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </header>

              <div className="nationalization-form-grid">
                <label className="nationalization-form-field">
                  <span>{isSaudiSetup
                    ? t('profile.fields.qiwa_entity_name', 'Qiwa entity name')
                    : t('profile.fields.establishment_name', 'Company / branch name')}<b>*</b></span>
                  <input
                    value={establishment.name || ''}
                    placeholder={isSaudiSetup
                      ? t('setup.saudiEntityNamePlaceholder', 'Enter the name shown in Qiwa')
                      : t('setup.establishmentNamePlaceholder', 'Enter a company or branch name')}
                    onChange={(event) => updateEstablishment(index, (current) => ({
                      ...current,
                      name: event.target.value
                    }))}
                  />
                </label>
                {profileFields
                  .filter((field) => !(establishment.excluded_by_authority === true && field.key.startsWith('official_target.')))
                  .filter((field) => targetFieldIsVisible(field, establishment))
                  .map((field) => (
                  <ProfileField
                    key={`${establishment.id}-${field.key}`}
                    field={{ ...field, required: fieldIsRequired(field, establishment) }}
                    language={language}
                    t={t}
                    idSuffix={establishment.id || index}
                    value={getAtPath(establishment, field.key)}
                    onChange={(value) => updateEstablishment(index, (current) => setAtPath(current, field.key, value))}
                  />
                  ))}
              </div>
            </article>
          ))}
        </div>

        <button type="button" className="nationalization-add-establishment" onClick={() => {
          setProfile((current) => ({
            ...current,
            ...(() => {
              const newEstablishment = makeEstablishment(current.country_code, current.establishments.length);
              return {
                establishments: [...current.establishments, newEstablishment],
                primary_establishment_id: current.primary_establishment_id || newEstablishment.id
              };
            })()
          }));
          setSetupPage(1);
        }}>
          <i className="fa-solid fa-plus" aria-hidden="true" />
          {isSaudiSetup
            ? t('actions.addSaudiEntity', 'Add another Qiwa entity')
            : t('actions.addEstablishment', 'Add another company / branch')}
        </button>
        <p className="nationalization-add-establishment-help">
          {isSaudiSetup
            ? t('setup.addSaudiEntityHelp', 'Add another only when Qiwa shows a different entity number or Nitaqat activity.')
            : t('setup.addEstablishmentHelp', 'Add another branch only if it has different official details.')}
        </p>
      </div>}

      {error && (
        <div className="nationalization-inline-error nationalization-setup-error" role="alert">
          <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
          <span>{uiText(error)}</span>
        </div>
      )}

      <div className="nationalization-form-actions">
        {setupPage === 1 ? (
          <button type="button" className="nationalization-secondary-button" onClick={() => setSetupPage(0)}>
            <i className={`fa-solid fa-arrow-${language === 'ar' ? 'right' : 'left'}`} aria-hidden="true" />
            {t('actions.back', 'Back')}
          </button>
        ) : onCancel && (
          <button type="button" className="nationalization-secondary-button" onClick={onCancel}>
            {t('actions.cancel', 'Cancel')}
          </button>
        )}
        {setupPage === 0 ? (
          <button
            type="button"
            className="nationalization-primary-button"
            disabled={!isCompanyStepValid}
            onClick={continueToGovernmentDetails}
          >
            {t('actions.continue', 'Continue')}
            <i className={`fa-solid fa-arrow-${language === 'ar' ? 'left' : 'right'}`} aria-hidden="true" />
          </button>
        ) : (
          <button
            type="button"
            className="nationalization-primary-button"
            disabled={!isValid || saving}
            onClick={() => onSave(profile)}
          >
            {saving ? t('actions.saving', 'Saving...') : t('actions.saveProfile', 'Save company profile')}
          </button>
        )}
      </div>
    </section>
  );
}
